import { createContext, useContext } from 'react'
import { type CrossClientQueryResult, CrossClientQueryResultMatches } from '../../../../models/CrossClient'
import { type CcqCard } from './ccqResultView'

// Matches the panel's exit animation in CcqRail.module.scss: it stays mounted this long after closing.
export const CCQ_PANEL_EXIT_MS = 120

// Fade-out before the list swaps in or out. Matches the exit animations in CcqRunView.module.scss and CcqGroupCard.module.scss.
export const CCQ_FULL_LIST_SWAP_MS = 180

export const CcqFullListTransitions = { OPENING: 'opening', CLOSING: 'closing' } as const

export type CcqFullListTransition = (typeof CcqFullListTransitions)[keyof typeof CcqFullListTransitions]

export interface CcqFullListSwap {
  kind: CcqFullListTransition
  card: CcqCard
}

export type CcqChangeableMatch = typeof CrossClientQueryResultMatches.MATCH | typeof CrossClientQueryResultMatches.NO_MATCH

export const CcqActionFilters = { ALL: 'all', WITH: 'with', WITHOUT: 'without' } as const

export type CcqActionFilter = (typeof CcqActionFilters)[keyof typeof CcqActionFilters]

export const hasActionsFor = (filter: CcqActionFilter): boolean | undefined => (filter === CcqActionFilters.ALL ? undefined : filter === CcqActionFilters.WITH)

export const CcqPanelOrigins = { PREVIEW: 'preview', FULL_LIST: 'fullList' } as const

export type CcqPanelOrigin = (typeof CcqPanelOrigins)[keyof typeof CcqPanelOrigins]

export interface CcqPanelState {
  resultId: string
  // The card the row was opened from: a moved client still shows in the one they left.
  card: CcqCard
  origin: CcqPanelOrigin
}

export interface CcqPanelPosition {
  position: number
  total: number
}

export interface CcqWorkspace {
  expanded: Record<CcqCard, boolean>
  toggleCard: (card: CcqCard) => void
  previews: Record<CcqCard, CrossClientQueryResult[]>
  previewsLoaded: Partial<Record<CcqCard, true>>
  previewsFailed: Partial<Record<CcqCard, true>>
  retryPreviews: (card: CcqCard) => void
  // The client the rail shows: the open one, or the one still sliding out after the panel closed.
  panel: CcqPanelState | null
  panelResult: CrossClientQueryResult | null
  isPanelClosing: boolean
  // Changes the match by hand. Resolves once the change is saved, or undone when it couldn't be.
  moveClient: (result: CrossClientQueryResult, match: CcqChangeableMatch) => Promise<void>
  // The card the check put a client the advisor moved in; their row names it in place of the criteria. Null when not moved.
  movedFrom: (result: CrossClientQueryResult) => CcqCard | null
  refreshPreviews: () => void
  selection: string[]
  toggleSelected: (resultId: string) => void
  deselect: (resultIds: string[]) => void
  setSelection: (resultIds: string[]) => void
  clearSelection: () => void
  // Each is true once, for the moment the panel was opened or the card's full list closed (any card's, when none is given).
  takePanelFocusRequest: () => boolean
  takeListCloseFocus: (card?: CcqCard) => boolean
}

export const CcqWorkspaceContext = createContext<CcqWorkspace | null>(null)

export const useCcqWorkspace = (): CcqWorkspace => {
  const value = useContext(CcqWorkspaceContext)
  if (value === null) {
    throw new Error('useCcqWorkspace must be used inside CcqWorkspaceProvider')
  }
  return value
}
